import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Accordion } from "../../../../components/controls/accordion";
import GridWrapper from "../../../../components/controls/GridWrapper";
import { DateInput, TextInput } from "../../../../components/controls/inputs";
import BaseDocumentCreator from "../../../../components/documents/baseDocumentCreator";
import styles from "../styles/ActDisagregation.module.scss";
import { v4 as uuidv4, NIL as uuidNull } from "uuid";
import { IDocumentUpdateFormProps } from "../../../../@types/documents";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { CheckBox } from "../../../../components/controls/checkbox";
import GridSelectorInput from "../../../../components/controls/GridSelectorInput";
import { StoreSelectorModal } from "../../../Dictionaries/Store";
import { LotsSelectorModal } from "../../../Accounting/Lots";
import { IOption, Select } from "../../../../components/selects/select";
import useGridFilter from "../../../../system/hooks/useGridFilter";
import { useForm } from "../../../../system/hooks/useForm";
import { ICostInfoDTO, IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common";
import ActDisagregationItemCreatorGridPluginSettings from "./ActDisagregationItemCreator/ActDisagregationItemCreatorGridPluginSettings";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { ILotDTO } from "../../../../libs/coreapi-dto/accounting/lot";
import { LotDataProvider } from "../../../../Services/DataProviders/LotDataProvider";
import { StoreDataProvider } from "../../../../Services/DataProviders/StoreDataProvider";
import { IActDisagregationCreateDTO, IActDisagregationGetDTO, IActDisagregationUpdateDTO } from "../../../../libs/coreapi-dto/documents/actDisagregation";
import { IActDisagregationItemCreateDTO, IActDisagregationItemEditDTO, IActDisagregationItemGetDTO } from "../../../../libs/coreapi-dto/documents/actDisagregationItem";
import KizScanModal from "../../../Dictionaries/Kiz/ScanModal/KizScanModal";
import { IKeyValuePair } from "../../../Reports/Shared/Interfaces/IKeyValuePair";
import { ActDisagregationUpdateItemModal } from "./ActDisagregationItemCreator/ActDisagregationUpdateItemModal";
import { DocumentType, IdTableVariant, SearchOption } from "../../../../@types/enumsGlobal";
import PositionEditPanel from "../../../../components/commandsPanels/PositionEditPanel";
import { ScalingRatioDataProvider } from "./../../../../Services/DataProviders/ScalingRatioDataProvider";
import { IScalingRatioDTO, IScalingRatioViewDTO } from "../../../../libs/coreapi-dto/dirs/scalingRatio";
import { ActDisagregationDataProvider } from "./../../../../Services/DataProviders/ActDisagregationDataProvider";
import { autocomplete } from "../../../../system/functions/autocomplete";
import { IPricingDocumentItemDTO } from "../../../../libs/coreapi-dto/dirs/pricingInvoice";
import { IKiz2DocumentGetDTO, IKizBox2DocumentItemValidationRequest, IKizBoxDTO, IKizConfirmationData, IKizDTO } from "../../../../libs/coreapi-dto/dirs/kiz";
import { KizDataProvider } from "../../../../Services/DataProviders/KizDataProvider";
import { KizBoxDataProvider } from "../../../../Services/DataProviders/KizBoxDataProvider";
import { PricingDisaggregationDataProvider } from "../../../../Services/DataProviders/PricingDisaggregationDataProvider";
import { controlKiz, countKizSum } from "../../../../system/functions/controlKiz";
import { ChangeableGrid } from "../../../../components/grids/changeableGrid/ChangeableGrid";
import { StoreSelectorPanel } from "../../../../components/storeSelectorPanel";
import { onCanChangeData } from "../../../../system/functions/onCanChangeData";
import { onPharmacyChanged } from "../../../../system/functions/onPharmacyChanged";
import { checkStorePharmacy } from "../../../../system/functions/checkStorePharmacy";
import useOperativeReserve from "../../../../components/lotSelector/useOperativeReserve";
import { IKizs } from "../../../../libs/core-api-requests/customRequest/GetKizsForDocumentRequest";
import { GoodsDataProvider } from "../../../../Services/DataProviders/GoodsDataProvider";
import { MessageModalWindow } from "../../../../components/modalWindows/MessageModalWindow";
import renderGlobalAlert from "../../../../system/hooks/useGlobalAlert";
import { validateDuplicateKizs } from "../../../../system/functions/validateDuplicateKizs";
import { KizDocumentDataProvider } from "../../../../Services/DataProviders/KizDocumentDataProvider";
import useLockingDocuments from "../../../../components/lockDocuments/useLockingDocuments";
import { MoneyFormatComponent } from "../../../../components/controls/inputs/BaseInput";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { checkLockStatus } from "../../../CommonHelperFunctions";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import { DictionaryInput } from "../../../../components/controls/dictionaryInput";
import { SelectDocumentModalWindow } from "../../../../components/modalWindows/SelectDocumentModalWindow";
import { ProgressActionSpinner } from "../../../../components/progressActionSpinner/ProgressActionSpinner";
import { IGoodsDTO, IGoodsGnvls } from "../../../../libs/coreapi-dto/dirs/goods";
import FlexRow from "../../../../components/controls/FlexRow";
import { ContractorDataProvider } from "../../../../Services/DataProviders/ContractorDataProvider";
import { IContractorDTO } from "../../../../libs/coreapi-dto/dirs/contractor";
import { actDisagregationKizBatchTrusted } from "../../../../libs/core-api-requests/customRequest/ActDisagregationKizBatchTrusted";
import { IStoreDTO } from "../../../../libs/coreapi-dto/dirs/store";
import jwtDecode from "jwt-decode";
import { promisifyMethod, promisifyMethodWithFallback } from "../../../../system/functions/promisifyMethod";
import { KizBoxEntity } from "../../../Dictionaries/Kiz/KizBoxEntity";
import MarkConfirmationModal, { MarkConfirmationType } from "../../../Dictionaries/Kiz/ScanModal/MarkConfirmationModal";

interface IValidator {
    store: IEntitySimpleDTO;
    documentDate: DateTime;
}

type CreatorItemMode = "init" | "edit";

export interface ICreatorItem {
    show: boolean;
    item: ILotDTO | null;
    editItem?: IActDisagregationItemEditDTO | null;
    mode?: CreatorItemMode,
    trusted: boolean
    autoConfirm?: boolean
}

export interface IDisplayItem {
    idGlobal: string;
    idLotFromGlobal: string;
    goodsName: string;
    quantity: number;
    scalingRatioFrom: string;
    scalingRatioTo: string;
    supplierPrice: number;
    supplierSum: number;
    quantityFrom: number;
    isKiz: boolean;
    isGnvls: boolean;
    countKizScan?: number;
    supplier?: string;
    retailPriceIncVat: number;
    lotRetailPriceIncVat: number;
    dateCreated: DateTime
    goodsCode: string,
    producer: string,
    financialSecurityCode: string
}

export interface ICreateItem {
    item: IActDisagregationItemCreateDTO;
    trusted: boolean;
    displayItem: IDisplayItem;
    autoConfirm?: boolean
}

interface IBaseFields {
    store: IEntitySimpleDTO;
    pharmacy: IEntitySimpleDTO;
    storeFromLot: IEntitySimpleDTO;
}

interface IReturnCreatedItem {
    createdItem: IActDisagregationItemCreateDTO;
    displayItem: IDisplayItem;
}

interface IMessageModalProps {
    show: boolean;
    message: string;
    btSaveOrOk?: BtSaveOrOk;
    item?: IActDisagregationItemCreateDTO
}

interface ISum {
    amountSupplierSum: number,
    amountSupplierVatSum: number,
    amountSupplierSumIncVat: number,
    amountRetailSum: number,
    amountRetailVatSum: number,
    amountRetailSumIncVat: number
}

type BtSaveOrOk = "ok" | "save";

const defaultGridFilterLot = (): IGridFilter => {
    return {
        numberPerPage: 15,
        pageNumber: 1,
        columnFilters: [
            {
                name: "isKizBox",
                operator: "Eq",
                value: true,
                invisible: true,
            },
        ],
    };
};

export const ActDisagregationUpdateForm: FC<IDocumentUpdateFormProps<IActDisagregationGetDTO, IActDisagregationItemGetDTO[], IActDisagregationUpdateDTO>> = (props) => {
    const appContext = useAppContext();

    // Data providers
    //const actDisagregationDP = new ActDisagregationDataProvider(appContext.coreApiService);
    const lotDP = new LotDataProvider(appContext.coreApiService);
    const scalingRatioDP = new ScalingRatioDataProvider(appContext.coreApiService);
    const storeDP = new StoreDataProvider(appContext.coreApiService);
    const kizDP = new KizDataProvider(appContext.coreApiService);
    const kizBoxDP = new KizBoxDataProvider(appContext.coreApiService);
    const pricingDP = new PricingDisaggregationDataProvider(appContext.coreApiService);
    const kizDocumentDP = new KizDocumentDataProvider(appContext.coreApiService);
    const contractorDP = new ContractorDataProvider(appContext.coreApiService);

    const userContext = useUserContext();
    const lockFromPermission = checkLockStatus(props.permission as IPermission, userContext.userPermission);
    const [onlyRead, setOnlyRead] = useState(
        props.variant === 'edit' &&
        (props.document?.documentState === 'proc' || lockFromPermission)
    );
    const idTable = IdTableVariant.ActDisagregation
    const lockingDocuments = useLockingDocuments();
    const [idLockingDocument, setIdLockingDocument] = useState<string|undefined>();

    const [disagregationMode, setDisagregationMode] = useState<boolean>(false);
    const [scanMatchMode, setScanMatchMode] = useState<boolean>(false);

    const jwtToken = localStorage.getItem("TokenData");
    const decodedToken: DecodedToken = jwtDecode(jwtToken as string);
    
    //const hasContractorFullAccess = decodedToken.permission.some(permission => 
    //    permission === "Contractor.FullAccess"
    //);

    const hasTrustedSupplierFullAccess = decodedToken.permission.some(permission => 
        permission === "ActDisagregationTrustedSupplier.FullAccess"
    );
    
    const roles = (userContext.userRoles || [])
    .flat()    
    .map(role => role.trim());  
    
    const isAdministrator = roles.some(role =>
        role.toLowerCase() === 'administrator'
    );
     
    const [documentData, setDocumentData] = useState<IActDisagregationUpdateDTO>(
        props.documentUpdate ??
            ({
                documentDate: DateTime.now(),
                isNewBarcode: false,
                items: [] as IActDisagregationItemCreateDTO[],
                idGlobal: props.idGlobal,
                isUnpackRecursive: true,
                trustedSupplier: false
            } as IActDisagregationCreateDTO)
    );

    const [sum, setSum] = useState<ISum>({
        amountSupplierSum: 0,
        amountSupplierVatSum: 0,
        amountSupplierSumIncVat: 0,
        amountRetailSum: 0,
        amountRetailVatSum: 0,
        amountRetailSumIncVat: 0
    });

    const [displayItems, setDisplayItems] = useState<IDisplayItem[]>([]);
    const [creatorItem, setCreatorItem] = useState<ICreatorItem>({ show: false, item: null, trusted: false });
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedScalingRatio, setSelectedScalingRatio] = useState<IScalingRatioDTO>({} as IScalingRatioDTO);
    const trustedCache = useRef(new Map<string, boolean>());
    const [selectedItemWithTrusted, setSelectedItemWithTrusted] = useState<IGridRow & { trusted?: boolean }>();

    const [baseFields, setBaseFields] = useState<IBaseFields>({
        store: {} as IEntitySimpleDTO,
        pharmacy: {} as IEntitySimpleDTO,
        storeFromLot: {} as IEntitySimpleDTO
    });
    const [showKizScan, setShowKizScan] = useState<boolean>(false);
    const [enableKizScan, setEnableKizScan] = useState<boolean>(true);

    const [gridFilter, dispatchGridFilter] = useGridFilter();
    const [gridFilterLot, dispatchGridFilterLot] = useGridFilter(defaultGridFilterLot());

    const [scanData, setScanData] = useState<string>("");
    const [pricingModel] = useState<IEntitySimpleDTO>({ idGlobal: uuidv4(), displayName: "--" });

    const [kizBoxes, setKizBoxes] = useState<IKizBoxDTO[]>();
    const [kizs, setKizs] = useState<IKizDTO[]>();
    const [palettes, setPalettes] = useState<IKizBoxDTO[]>();

    const [scalingRatioFrom, setScalingRatioFrom] = useState<IScalingRatioDTO>({} as IScalingRatioDTO);
    const [scalingRatioTo, setScalingRatioTo] = useState<IScalingRatioDTO>();

    const goodsDP = new GoodsDataProvider(appContext.coreApiService);
    const [isGnvls, setIsGnvls] = useState<boolean>(false);

    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.actDisagregation." + value);
    const errorsT = (value: string) => t("errors." + value);

    const defaultSearchOption = {displayName: t("documents.actDisagregation.kizOrKizBox"), value: SearchOption.kizBox} as IOption;
    const [searchOption, setSearchOption] = useState<IOption>(defaultSearchOption);

    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({ show: false, message: "" });

    const [showMessageModalValidDocument, setShowMessageModalValidDocument] = useState<IMessageModalProps>({ show: false, message: "" });
    const [showMessageModalWarning, setShowMessageModalWarning] = useState<IMessageModalProps>({ show: false, message: "" });
    const [autoFocus, setAutoFocus] = useState<boolean>(false)

    const tabsContext = useTabsContext();
    const currentIdTab = useRef<string>('');

    // Base document
    const [displayNameBaseDoc, setDisplayNameBaseDoc] = useState<string>(props.document?.baseDocument?.displayName ?? "");
    const [showMessageModalValidBaseDocument, setShowMessageModalValidBaseDocument] = useState<boolean>(false);
    const [selectBaseDocument, setSelectBaseDocument] = useState<boolean>(false);

    // Состояние модального окна
    const [modalWindow, setModalWindow] = useState(<></>);

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            store: {
                required: {
                    value: documentData?.idStoreGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            documentDate: {
                required: {
                    value: documentData?.documentDate?.day ? false : true,
                    message: errorsT("required")
                }
            }
        },
    });

    const getGnvlsPriceFromPricing = (calcItem: IPricingDocumentItemDTO): number => {

        return getGnvlsPriceFromLotValues(calcItem.retailPriceWithVat, calcItem.numerator, calcItem.denominator);
    };

    const getGnvlsPriceFromLotValues = (retailPriceWithVat: number, numerator: number, denominator: number): number => {

        let priceGnvls = (retailPriceWithVat / numerator) * denominator;
        priceGnvls = parseFloat(priceGnvls.toFixed(6));
        priceGnvls = Math.floor(priceGnvls * 100) / 100;

        return priceGnvls;
    };

    const returnCreateItem = async (): Promise<ICreateItem | null> => {
        if (!creatorItem.item || !scalingRatioTo) {
            return null;
        }

        const idGlobal: string = uuidv4();
        let lot = creatorItem.item;

        // Подгрузка признака GNVLS из Goods
        const gnvlsMark: IGoodsGnvls = await loadGoodsGnvls(lot.goods.idGlobal);
        const retailPriceIncVat: number = !gnvlsMark?.important ?
            lot.retailCostInfo?.priceIncVat ? lot.retailCostInfo?.priceIncVat / lot.numerator : 0 :
            getGnvlsPriceFromLotValues(lot.retailCostInfo?.priceIncVat, lot.numerator, lot.denominator);

        const itemContractor: IContractorDTO = await promisifyMethod<IContractorDTO, typeof contractorDP>(
            contractorDP,
            contractorDP.getById,
            lot.contractor.idGlobal
        );

        return {
            item: {
                idGlobal: idGlobal,
                idGoodsGlobal: lot.goods.idGlobal,
                idLotFromGlobal: lot.idGlobal,
                idScalingRatioFromGlobal: scalingRatioFrom.idGlobal,
                kizs: kizs,
                producerPrice: lot.producerPrice,
                kizBoxes: kizBoxes,
                palettes: palettes,
                retailCostInfo: {
                    adprice: lot.retailCostInfo?.adprice ?? 0,
                    vatPrice: lot.retailCostInfo?.vatPrice ? lot.retailCostInfo?.vatPrice / lot.numerator : 0,
                    vat: lot.retailCostInfo?.vat ?? 0,
                    vatSum: lot.retailCostInfo?.vatSum ? lot.retailCostInfo?.vatSum / lot.quantityAdd : 0,
                    sumIncVat: lot.retailCostInfo?.sumIncVat ? lot.retailCostInfo?.sumIncVat / lot.quantityAdd: 0,
                    sum: lot.retailCostInfo?.sum ? lot.retailCostInfo?.sum / lot.quantityAdd: 0,
                    price: lot.retailCostInfo?.price ? lot.retailCostInfo?.price / lot.numerator : 0,
                    priceIncVat: retailPriceIncVat,
                },
                supplierCostInfo: {
                    adprice: lot.supplierCostInfo?.adprice ?? 0,
                    vatPrice: lot.supplierCostInfo?.vatPrice ? lot.supplierCostInfo?.vatPrice / lot.numerator : 0,
                    vat: lot.supplierCostInfo?.vat ?? 0,
                    vatSum: lot.supplierCostInfo?.vatSum ? lot.supplierCostInfo?.vatSum / lot.quantityAdd : 0,
                    sumIncVat: lot.supplierCostInfo?.sumIncVat ? lot.supplierCostInfo?.sumIncVat / lot.quantityAdd  : 0,
                    sum: lot.supplierCostInfo?.sum ? lot.supplierCostInfo?.sum / lot.quantityAdd  : 0,
                    price: lot.supplierCostInfo?.price ? lot.supplierCostInfo?.price / lot.numerator : 0,
                    priceIncVat: lot.supplierCostInfo?.priceIncVat ? lot.supplierCostInfo?.priceIncVat / lot.numerator : 0,
                },
                productMargin: lot.productMargin,
                quantityFrom: 1,
                quantity: lot.numerator,
                idScalingRatioToGlobal: scalingRatioTo.idGlobal,
                isKiz: lot.isKiz,
                isKizBox: documentData.isUnpackRecursive ? lot.isKizBox : true,
                denominator: lot.denominator,
                numerator: lot.numerator,
                kizBoxCount: kizBoxes?.length ?? 0,
                kizCount: kizs?.length ?? 0,
                isGnvls: gnvlsMark?.important,
                dateCreated: DateTime.now()
            } as IActDisagregationItemCreateDTO,
            displayItem: {
                idGlobal: idGlobal,
                idLotFromGlobal: lot.idGlobal,
                goodsName: lot.goods.displayName,
                goodsCode: lot.goodsCode,
                producer: lot.producer.displayName,
                quantityFrom: 1,
                quantity: scalingRatioFrom.numerator,

                scalingRatioFrom: scalingRatioFrom.displayName,
                scalingRatioTo: scalingRatioTo.displayName,

                retailPrice: lot.retailCostInfo?.price ? lot.retailCostInfo?.price / lot.numerator : 0,

                supplierPrice: lot.supplierCostInfo?.price ? lot.supplierCostInfo?.price / lot.numerator : 0,
                supplierSum: lot.supplierCostInfo?.sum ? lot.supplierCostInfo?.sum / lot.quantityAdd : 0,

                retailSumWithVat: lot.retailCostInfo?.sumIncVat ? lot.retailCostInfo?.sumIncVat / lot.quantityAdd : 0,
                retailPriceIncVat: retailPriceIncVat,

                lotRetailPriceIncVat: lot.retailCostInfo?.priceIncVat ?? 0,

                isGnvls: gnvlsMark?.important,
                isKiz: true,
                isKizBox: documentData.isUnpackRecursive ? lot.isKizBox : true,
                dateCreated: DateTime.now(),
                financialSecurityCode: lot?.financialSecurity?.displayName
            } as IDisplayItem,
            trusted: itemContractor.attributesInfo.trustedSupplier
        };
    };

    const sendOnlineReserve = (idLotFromGlobal: string, idGlobalItem: string, quantity: number, callback: () => void, fallback?: () => void) => {
        operativeReserve.send(
            {
                idLot: idLotFromGlobal,
                idDocument: documentData.idGlobal as string,
                idTable: IdTableVariant.ActDisagregation,
                idDocumentItem: idGlobalItem,
                quantity: quantity,
            },
            callback,
            fallback
        );
    };

    const loadScalingRatioTo = (idGoodsGlobal: string) => {
        const setScalingRatioToWithNumerator = (numerator: number) => {
            scalingRatioDP.overrideGetView(
                idGoodsGlobal,
                {
                    pageNumber: 1,
                    numberPerPage: 1,
                    columnFilters: [
                        { name: "numerator", operator: "Eq", value: numerator.toString() } as IViewFilter, 
                        { name: "denominator", operator: "Eq", value: "1" } as IViewFilter
                    ],
                } as IViewBody,
                (e) => {
                    if (e && e.length > 0) {
                        scalingRatioDP.overrideGetById(idGoodsGlobal, e[0].idGlobal, (ee) => {
                            setScalingRatioTo({
                                idGlobal: ee.idGlobal,
                                displayName: ee.displayName,
                                unit: ee.unit,
                                numerator: ee.numerator,
                                denominator: ee.denominator,
                            });
                        });
                    }
                }
            );
        }

        const itemForKiz : IActDisagregationItemCreateDTO = getItemForKiz();
        if (documentData.isUnpackRecursive || itemForKiz === undefined) {
            setScalingRatioToWithNumerator(1);
        } else {
            const idParentGlobal = itemForKiz.palettes?.find(x => x.idParent === null)?.idGlobal as string;
            if (idParentGlobal.length > 0) {
                kizBoxDP.findChildren(idParentGlobal, (packedBoxes: IKizBoxDTO[]) => {
                    if (packedBoxes.length > 0) {
                        setScalingRatioToWithNumerator(packedBoxes.length);
                    }
                });
            } else {
                setScalingRatioToWithNumerator(1);
            }
        }
        
    };

    useEffect(() => {
        if (documentData?.items) {
            setSum({
                ...sum,
                amountSupplierSum: documentData.items.reduce((sum, item) => sum + item.supplierCostInfo.sum, 0),
                amountSupplierVatSum: documentData.items.reduce((sum, item) => sum + item.supplierCostInfo.vatSum, 0),
                amountSupplierSumIncVat: documentData.items.reduce((sum, item) => sum + item.supplierCostInfo.sumIncVat, 0),
                amountRetailSum: documentData.items.reduce((sum, item) => sum + item.retailCostInfo.sum, 0),
                amountRetailVatSum: documentData.items.reduce((sum, item) => sum + item.retailCostInfo.vatSum, 0),
                amountRetailSumIncVat: documentData.items.reduce((sum, item) => sum + item.retailCostInfo.sumIncVat, 0),
            });
        }
    }, [documentData?.items, displayItems]);

    useEffect(() => {
        setAutoFocus(!creatorItem.show)
    }, [creatorItem]);

    useEffect(() => {
        setDisagregationMode(displayItems.length > 0);
    }, [displayItems])

    useEffect(() => {
        if (tabsContext?.currentTab) {
            currentIdTab.current = tabsContext.currentTab?.id;
        }
        if (creatorItem.item) {
            goodsDP.getById(creatorItem.item.goods.idGlobal, (e) => {
                setIsGnvls(e.gnvls.important ?? false);
            });
        }
    }, []);

    const setRoundingGnvls = (e: IPricingDocumentItemDTO, newRetailPriceWithVat: number) => {

        return {
            ...e,
            retailPriceWithVat: !isGnvls ? e.retailPriceWithVat : newRetailPriceWithVat
        } as IPricingDocumentItemDTO
    };

    const requestPricingInvoiceTimeout = useRef<NodeJS.Timeout>();
    const calculated = (calcItem: IPricingDocumentItemDTO) => {

        clearTimeout(requestPricingInvoiceTimeout.current as NodeJS.Timeout);
        
        requestPricingInvoiceTimeout.current = setTimeout(() => {
            pricingDP.calculateItem(pricingModel.idGlobal, calcItem, (e) => {
                let priceGnvls = getGnvlsPriceFromPricing(calcItem);
                setRoundingGnvls(e, priceGnvls);
            });
        }, Number(process.env.REACT_APP_INTERVAL_REQUEST));
    };

    const loadScalingRatioFrom = async (lot: ILotDTO): Promise<void> => {

        scalingRatioDP.overrideGetById(lot.goods.idGlobal, lot.scalingRatio.idGlobal, async (sr) => {
            setScalingRatioFrom({
                idGlobal: sr.idGlobal,
                displayName: sr.displayName,
                unit: sr.unit,
                numerator: sr.numerator,
                denominator: sr.denominator,
            });

            const gnvlsMark: IGoodsGnvls = await loadGoodsGnvls(lot.goods.idGlobal);
            const retailPriceIncVat: number = !gnvlsMark?.important ?
                lot.retailCostInfo?.priceIncVat ? lot.retailCostInfo?.priceIncVat / lot.numerator : 0 :
                getGnvlsPriceFromLotValues(lot.retailCostInfo?.priceIncVat, lot.numerator, lot.denominator);

            calculated({
                quantity: 1,
                supplierVatRatePercentage: lot.supplierCostInfo.vat,
                supplierPriceExcludingVat: lot.supplierCostInfo.price / lot.numerator,
                supplierPriceWithVat: lot.supplierCostInfo.priceIncVat / lot.numerator,
                supplierVatAmount: lot.supplierCostInfo.vatSum / lot.quantityAdd,
                supplierAmountExcludingVat: lot.supplierCostInfo.sum / lot.quantityAdd,
                supplierAmountWithVat: lot.supplierCostInfo.sumIncVat / lot.quantityAdd,
                retailUnitMargin: lot.productMargin,
                percentageOfVatRateRetail: lot.retailCostInfo.vat,
                retailPrice: lot.retailCostInfo.price / lot.numerator,

                retailPriceWithVat: retailPriceIncVat,

                vatAmountRetail: lot.retailCostInfo.vatSum / lot.quantityAdd,
                retailAmount: lot.retailCostInfo.sum / lot.quantityAdd,
                retailAmountWithVat: lot.retailCostInfo.sumIncVat / lot.quantityAdd,
                numerator: sr.numerator,
                denominator: sr.denominator,
                eventType: "DenominatorChanged",
            } as IPricingDocumentItemDTO);
        });
    };

    const getScanKizCount = (): number => {
        let item = documentData.items.find((x) => x.idGlobal === selectedItem?.idGlobal);
        if (!item) {
            return 0;
        }
        return item.quantity;
    };

    type GetKizsResult = IKizBoxDTO[] | IKizs | undefined | 'cancelled';
    const addNewLot = (lot: ILotDTO, idDocument: string | null = null, idDocumentItem: string | null = null, ssccScanConfirmation: boolean = false) => {

        const targetIdDocument = idDocument ?? creatorItem.item?.documentInfo.idDocument as string;
        const targetIdDocumentItem = idDocumentItem ?? creatorItem.item?.documentInfo.idDocumentItem as string;

        const loadBoxEntries = (ssccValue: string) => {
            kizDP.getKizBySscc(ssccValue, (kizEntities) => {
                if (kizEntities.length === 0) {
                    renderGlobalAlert({
                        variant: "warning",
                        statusCode: 200,
                        title: baseT("boxEntriesNotDownloadedFromMDLP"),
                        isSound: true
                    });
                    setCreatorItem({ ...creatorItem, show: false })
                } else {
                    setKizs(kizEntities);
                    setCreatorItem({ ...creatorItem, show: true })
                }
            });
        }

        getKizs(targetIdDocument, targetIdDocumentItem, lot.lotName, ssccScanConfirmation).then((kiz: GetKizsResult) => {

            if (kiz === 'cancelled' || kiz === undefined) return;
            if (searchOption.value === SearchOption.kizBox && scanData.trim().length > 0) {

                if (documentData.isUnpackRecursive) {
                    setKizBoxes(kiz as IKizBoxDTO[]);
                    loadBoxEntries(scanData.substring(2));
                } else {
                    const paletteBoxes: IKizBoxDTO[] = (kiz as IKizBoxDTO[]).filter(x => x.idParent === null);
                    if (paletteBoxes.length > 0) {
                        setPalettes(paletteBoxes);

                        // Подгрузить короба вне зависимости от доверенного поставщика
                        // Доверенность регулируется через подтверждение SSCC
                        paletteBoxes.forEach((palette: IKizBoxDTO) => {
                            kizBoxDP.findChildren(palette.idGlobal, (kids: IKizBoxDTO[]) => {
                                if (kids.length > 0) {
                                    setKizBoxes(kids);
                                    setKizs(undefined);
                                }
                            })
                        });
                    }
                } 
            } else {

                let items = documentData.items.filter(c => c.idLotFromGlobal === lot.idGlobal)
                const filterByConfirmationMode = ssccScanConfirmation && ssccConfirmedRef.current.length > 0;

                if (targetIdDocument) {
                    kizDocumentDP.getKizByIdDocument(targetIdDocument, (entity: IKiz2DocumentGetDTO) => {

                        let kizBoxes = [] as IKizBoxDTO[]
                        let sscc = items.flatMap(c => c.kizBoxes).flatMap(c => c?.sscc)

                        let itemWithBox = entity.items.find(x => x.kizBoxes != null && x.idDocumentItemGlobal === targetIdDocumentItem);
                        if (itemWithBox) {
                            itemWithBox.kizBoxes.forEach(element => {

                                // Сверка с подтвержденным sscc
                                if (filterByConfirmationMode) {
                                    const isConfirmedMatch = element.sscc === ssccConfirmedRef.current;
                                    if (!isConfirmedMatch) {
                                        return;
                                    }
                                }

                                const isAlreadyExists = sscc.some(c => c === element.sscc);
                                if (!isAlreadyExists) {
                                    kizBoxes?.push(element);
                                }
                            });
                        }
                        else {
                            console.warn(`Ошибка подбора короба для партии '${lot.lotName}', ID документа прихода партии: '${targetIdDocument}'`)
                        }

                        if (kizBoxes.length > 0) {
                            if (documentData.isUnpackRecursive) {
                                setKizBoxes(kizBoxes)
                                loadBoxEntries(kizBoxes[0]?.sscc)
                            } else {
                                const palettes = kizBoxes.filter(x => x.idParent === null);
                                setPalettes(palettes);

                                if (palettes.length > 0) {
                                    kizBoxDP.findChildren(palettes[0].idGlobal, ((kidsBoxes: IKizBoxDTO[]) => {
                                        setKizBoxes(kidsBoxes);
                                    }));
                                }
                            }
                        }
                    }, () => {
                        // Присваеваем короб напрямую
                        // Пытаемся подгрузить состав
                        if (ssccScanConfirmation) {
                            setKizBoxes(kiz as IKizBoxDTO[])
                            loadBoxEntries(ssccConfirmedRef.current)
                        }
                    })
                }
            }

            let idGoodsGlobal = lot.goods.idGlobal;

            // выгрузка Scaling Ratio:
            // to: из goods
            // from: из lot
            if (idGoodsGlobal) {
                loadScalingRatioTo(idGoodsGlobal);
                loadScalingRatioFrom(lot);
            }
        });
    };

    const addKizInExistingItem = (items: IActDisagregationItemEditDTO[]) => {
        let scan = scanData.substring(2)
        let item = items[0]

        if (!item?.kizBoxes || item.kizBoxes?.length === 0) {
            const updateData = (kizBoxes: IKizBoxDTO[], kizs: IKizDTO[] | undefined) => {
                setDocumentData({
                    ...documentData, items: [...documentData.items.filter(c => c.idGlobal !== item.idGlobal),
                    {
                        ...item,
                        kizBoxes: kizBoxes,
                        kizs: undefined
                    }]
                });
                let displayItem = displayItems.find(c => c.idGlobal === item.idGlobal)
                if (displayItem) {
                    setDisplayItems([...displayItems.filter((x) => x.idGlobal !== item.idGlobal), 
                        { ...displayItem, 
                            countKizScan: documentData.isUnpackRecursive ? (kizs as IKizDTO[]).length : kizBoxes.length}])
                }
                renderGlobalAlert({
                    variant: "info",
                    statusCode: 200,
                    title: `${documentData.isUnpackRecursive ? "Кизы" : "короба"} успешно добавлены в партию`,
                })
            };
            kizBoxDP.getKizBySscc(scan, (kizBoxes: IKizBoxDTO[]) => {
                if (!documentData.isUnpackRecursive) {
                    updateData(kizBoxes, undefined);
                } else {
                    kizDP.getKizBySscc(scan, (kizs) => {
                        updateData(kizBoxes, kizs);
                    });
                }
            })
        }  
    }

    const duplicateMessage = () => {
        renderGlobalAlert({
            variant: "danger",
            statusCode: 500,
            title: `${scanData} SSCC уже добавлен в партию`,
            isSound: true
        })
    }

    useEffect(() => {
        dispatchGridFilterLot(onPharmacyChanged(baseFields.pharmacy.idGlobal));
    }, [baseFields.pharmacy]);

    useEffect(() => {
        if (!selectedItem) {
            setSelectedScalingRatio({} as IScalingRatioDTO);
            return;
        }

        const selectedItemDto = documentData.items.find((x) => x.idGlobal === selectedItem.idGlobal) as IActDisagregationItemCreateDTO;
        
        if (!selectedItemDto) {
            setSelectedScalingRatio({} as IScalingRatioDTO);
            return;
        }

        if (selectedItemDto.idGoodsGlobal && selectedItemDto.idScalingRatioFromGlobal) {
            scalingRatioDP.overrideGetById(
                selectedItemDto.idGoodsGlobal, 
                selectedItemDto.idScalingRatioFromGlobal, 
                (e) => setSelectedScalingRatio(e)
            );
        } else {
            setSelectedScalingRatio({} as IScalingRatioDTO);
        }
    }, [selectedItem]);

    const checkIfTrusted = useCallback(async (idDocumentItemGlobal: string, _idLotGlobal?: string, fallback?: () => void): Promise<boolean> => {
        try {

            const docItem: IActDisagregationItemCreateDTO = documentData
                .items
                .find((x) => x.idGlobal === idDocumentItemGlobal) as IActDisagregationItemCreateDTO

            const idLotGlobal = docItem !== undefined ? docItem.idLotFromGlobal :
                _idLotGlobal !== undefined ? _idLotGlobal : creatorItem.item?.idGlobal as string;

            const lot: ILotDTO = await promisifyMethod<ILotDTO, typeof lotDP>(
                lotDP,
                lotDP.getById,
                idLotGlobal
            );

            // Проверяем кэш для контрагента
            if (trustedCache.current.has(lot.idSupplierGlobal)) {
                const result =  trustedCache.current.get(lot.idSupplierGlobal)!;
                return result;
            }

            const contractor: IContractorDTO = await promisifyMethod<IContractorDTO, typeof contractorDP>(
                contractorDP,
                contractorDP.getById,
                lot.idSupplierGlobal
            );

            const isTrusted = contractor?.attributesInfo.trustedSupplier;

            // Обновляем кэш
            trustedCache.current.set(lot.idSupplierGlobal, isTrusted);

            return isTrusted;
        } catch (error) {
            fallback?.()
            console.error('Ошибка получения данных по доверенности поставщика:', error);
            return false;
        }
    }, [documentData, creatorItem]);

    useEffect(() => {
        const updateSelectedItemWithTrusted = async () => {
            setEnableKizScan(false)
            if (selectedItem) {
                try {
                    const trusted = await checkIfTrusted(selectedItem.idGlobal);
                    setSelectedItemWithTrusted({
                        ...selectedItem,
                        trusted
                    });
                    setEnableKizScan(true)
                } catch (error) {
                    setSelectedItemWithTrusted({
                        ...selectedItem,
                        trusted: false
                    });
                    setEnableKizScan(true)
                }
            } else {
                setSelectedItemWithTrusted(undefined);
                setEnableKizScan(true)
            }
        };

        updateSelectedItemWithTrusted();
    }, [selectedItem]);

    useEffect(() => {
        if (documentData?.idStoreGlobal) {
            storeDP.getById(documentData.idStoreGlobal, (store: IStoreDTO) => {
                setBaseFields({ 
                    ...baseFields, 
                    store: { 
                        displayName: store.name, 
                        idGlobal: documentData.idStoreGlobal as string }, 
                    pharmacy: store.contractor 
                });
                dispatchGridFilterLot({
                    type: "addColumnFilter",
                    payload: {
                        name: "idStoreGlobal",
                        operator: "Eq",
                        value: documentData.idStoreGlobal,
                        invisible: true,
                    },
                });
            });
        } else {
            setBaseFields({
                ...baseFields,
                store: {} as IEntitySimpleDTO,
            });

            dispatchGridFilterLot({
                type: "deleteColumnFilter",
                payload: "idStoreGlobal",
            });
        }
    }, [documentData?.idStoreGlobal]);

    // Блокировка документа
    const requestUpdateLockingDocumentTimeout = useRef<NodeJS.Timeout>();
    const isUpdatingLockingDocument = useRef(false);

    useEffect(() => {
        // Если документ не определён — ничего не делаем
        if (!idLockingDocument) return;

        // Если уже идёт обновление — не ставим новый таймер
        if (isUpdatingLockingDocument.current) return;

        // Отменяем предыдущий таймер
        if (requestUpdateLockingDocumentTimeout.current) {
            clearTimeout(requestUpdateLockingDocumentTimeout.current);
        }

        clearTimeout(requestUpdateLockingDocumentTimeout.current as NodeJS.Timeout);
        requestUpdateLockingDocumentTimeout.current = setTimeout(() => {

            isUpdatingLockingDocument.current = true;

            lockingDocuments.update(idLockingDocument, (e) => {
                isUpdatingLockingDocument.current = false;
            },
                () => {
                    isUpdatingLockingDocument.current = false;
                })
        }, Number(process.env.REACT_APP_INTERVAL_REQUEST));
    }, [documentData.documentDate, documentData.idGosContractGlobal, documentData.idStoreGlobal, documentData.idUserGlobal, documentData.items]);


    useEffect(() => {
        if (props.variant === "create") {
            autocomplete().then((e) => {
                e.contractorMe && setDocumentData({ ...documentData, idStoreGlobal: e.contractorMe?.store?.idGlobal });
            });
        }
        if (props.variant === "edit") {
            lockingDocuments.check(documentData.idGlobal as string, (e) => {
                if (e) setOnlyRead(true)
                else
                    lockingDocuments.send({
                        idTable: IdTableVariant.ActDisagregation,
                        idDocument: documentData.idGlobal as string,
                    }, (e) => {
                        setIdLockingDocument(e)
                    });
            })
        }
        
    }, []);

    useEffect(() => {
        const handleTabClose = (event) => {
            if (!onlyRead)
                operativeReserve.deleteAll({ idTable: IdTableVariant.ActDisagregation, idDocument: documentData.idGlobal as string });
            lockingDocuments.delete({
                idTable: IdTableVariant.ActDisagregation,
                idDocument: documentData.idGlobal as string,
            });
            return (event.returnValue = "");
        };

        window.addEventListener("beforeunload", handleTabClose);

        return () => {
            window.removeEventListener("beforeunload", handleTabClose);
        };
    }, []);

    const refreshCurrentDisplayItems = async () => {
        if (displayItems.length === 0) return

        const findItem = (idGlobal: string) =>
            documentData.items.find((item) => item.idGlobal === idGlobal);

        const countScannedKiz = (kizList: IKizConfirmationData[] | undefined) =>
            kizList?.filter(ks => ks.infoScanned?.length > 0).length ?? 0;

        const updatedItems = await Promise.all(
            displayItems.map(async (x: IDisplayItem) => {
                const item: IActDisagregationItemEditDTO | undefined = findItem(x.idGlobal);
                const trusted = await checkIfTrusted(item?.idGlobal as string);

                return {
                    ...x,
                    countKizScan: documentData.isUnpackRecursive
                        ? documentData.trustedSupplier || trusted
                            ? item?.kizCount
                            : countScannedKiz(item?.kizConfirmationScanned)
                        : documentData.trustedSupplier || trusted
                            ? item?.kizBoxCount
                            : countScannedKiz(item?.kizConfirmationScanned)
                };
            })
        );
        setDisplayItems(updatedItems);
    }

    useEffect(() => {
        if (props.documentItems) {
            setDisplayItems([
                ...props.documentItems.map((x: IActDisagregationItemGetDTO): IDisplayItem => {
                    if (!onlyRead)
                        operativeReserve.send({
                            idLot: x.lotFrom.idGlobal,
                            idDocument: documentData.idGlobal as string,
                            idTable: IdTableVariant.ActDisagregation,
                            idDocumentItem: x.idGlobal,
                            quantity: x.quantityFrom,
                        });

                    const findItem = (idGlobal: string) =>
                        documentData.items.find((item) => item.idGlobal === idGlobal);

                    const countScannedKiz = (kizList: IKizConfirmationData[] | undefined) =>
                        kizList?.filter(ks => ks.infoScanned?.length > 0).length ?? 0;

                    return {
                        idGlobal: x.idGlobal,
                        idLotFromGlobal: x.lotFrom.idGlobal,
                        goodsName: x.goods.displayName,
                        goodsCode: x.goodsCode,
                        producer: x.producer,
                        quantity: x.quantity,
                        scalingRatioFrom: x.scalingRatioFrom.displayName,
                        scalingRatioTo: x.scalingRatioTo.displayName,
                        supplierPrice: x.supplierCostInfo.price,
                        supplierSum: x.supplierCostInfo.sum,
                        quantityFrom: x.quantityFrom,
                        isKiz: true,
                        isKizBox: true,
                        isGnvls: false,
                        supplier: x.supplier,
                        retailPriceIncVat: x.retailCostInfo?.priceIncVat ?? 0,
                        lotRetailPriceIncVat: x.retailCostInfo.sumIncVat,  /*x.retailCostInfo?.priceIncVat * x.quantity,*/
                        dateCreated: x.dateCreated,
                        financialSecurityCode: x?.financialSecurity?.displayName,
                        countKizScan: documentData.isUnpackRecursive
                            ? documentData.trustedSupplier
                                ? x.kizCount
                                : countScannedKiz(findItem(x.idGlobal)?.kizConfirmationScanned)
                            : documentData.trustedSupplier
                                ? x.kizBoxCount
                                : countScannedKiz(findItem(x.idGlobal)?.kizConfirmationScanned)
                    } as IDisplayItem;
                }),
            ]);
        }
    }, []);

    const presetTrustedForBoxParent = async (box: IKizBoxDTO, idDocumentItem: string, isUnpackRecursive: boolean = true) => {

        const trusted: boolean = await checkIfTrusted(idDocumentItem);

        if (isUnpackRecursive) {
            
            // Предустановка для КИЗ
            kizDP.setTrustedForParent(box.idGlobal, trusted);
        } else {

            // Предустановка для коробов - случай с палетами
            kizBoxDP.setTrustedForParent(box.idGlobal, trusted);
        }
    }

    // Валидация SSCC
    const createSsccValidator = (idDocument: string, idDocumentItem: string) => {
        return async (sscc: string): Promise<boolean> => {
            try {
                if (!KizBoxEntity.isKizBox(sscc)) {
                    return false;
                }

                const validateResult = await promisifyMethodWithFallback<boolean, typeof kizBoxDP>(
                    kizBoxDP,
                    kizBoxDP.validateDocumentItemBySscc,
                    {
                        sscc,
                        idDocument,
                        idDocumentItem
                    } as IKizBox2DocumentItemValidationRequest
                );

                return validateResult;
            } catch (error) {
                return false;
            }
        };
    };

    const showSsccConfirmationModal = (
        lotName: string | undefined,
        idDocument: string,
        idDocumentItem: string,
        onConfirm: (ssccValue: string) => void,
        onClose: () => void
    ): void => {
        const validator = createSsccValidator(idDocument, idDocumentItem);

        setModalWindow(
            <MarkConfirmationModal
                headerText={`Отсканируйте SSCC для '${lotName}'`}
                scanText=""
                onConfirm={onConfirm}
                validate={validator}
                onClose={onClose}
                markType={MarkConfirmationType.Box}
            >
            </MarkConfirmationModal>
        );
    };

    const ssccConfirmedRef = useRef('');

    const getKizs = (
        idDocument: string,
        idDocumentItem: string,
        lotName?: string,
        ssccScanConfirmation: boolean = false
    ): Promise<GetKizsResult> => {

        // Загрузка короба по SSCC
        const loadBoxEntries = (ssccValue: string): Promise<GetKizsResult> => {
            return new Promise((resolve) => {
                kizBoxDP.getKizBySscc(ssccValue, async (entities: IKizBoxDTO[]) => {

                    // Сохраняем ссылку на sscc подтверждения
                    ssccConfirmedRef.current = ssccValue;

                    // Предустановка trusted для вложенных партий
                    if (entities.length > 0 && !documentData.trustedSupplier) {
                        await presetTrustedForBoxParent(entities[0], idDocumentItem, documentData.isUnpackRecursive);
                    }
                    resolve(entities || []);
                });
            });
        };

        // Обработчики модального окна
        const handleConfirm = (ssccValue: string) => {
            setModalWindow(<></>);
            return loadBoxEntries(ssccValue);
        };

        const handleClose = () => {
            setModalWindow(<></>);
            return Promise.resolve('cancelled' as GetKizsResult);
        };

        // Извлекаем SSCC из scanData (если используется SearchOption.kizBox)
        const sscc = searchOption.value === SearchOption.kizBox ? scanData.substring(2) : '';
        if (searchOption.value === SearchOption.kizBox) {
            if (sscc.trim().length > 0) {
                return loadBoxEntries(sscc);
            } else if (ssccScanConfirmation) {
                showSsccConfirmationModal(lotName, idDocument, idDocumentItem, handleConfirm, handleClose);

                // Модальное окно вызывает resolve через handleConfirm/handleClose
                // Поэтому возвращаем "ожидающий" промис
                return new Promise((resolve) => {

                    // Подменяем resolve в модальном окне через замыкание
                    const wrappedHandleConfirm = (valueStr: string) => resolve(handleConfirm(valueStr));
                    const wrappedHandleClose = () => resolve(handleClose());
                    showSsccConfirmationModal(lotName, idDocument, idDocumentItem, wrappedHandleConfirm, wrappedHandleClose);
                });
            }
        } else if (ssccScanConfirmation) {

            // Для других searchOption показываем модалку, если нужно подтверждение
            return new Promise((resolve) => {
                const wrappedHandleConfirm = (val: string) => resolve(handleConfirm(val));
                const wrappedHandleClose = () => resolve(handleClose());
                showSsccConfirmationModal(lotName, idDocument, idDocumentItem, wrappedHandleConfirm, wrappedHandleClose);
            });
        }
        
        return Promise.resolve([] as GetKizsResult);
    };

    useEffect(() => {
        returnCreateItem().then((item) => {
            if (item) {
                const autoConfirm: boolean = creatorItem.mode !== 'edit';
                setCreatorItem({ ...creatorItem, editItem: item.item, trusted: item.trusted, autoConfirm });
            }
        });

    }, [scalingRatioTo]);

    useEffect(() => {
        if (creatorItem.item)
            addNewLot(creatorItem.item, null, null, creatorItem.mode !== 'edit');

    }, [creatorItem.item]);

    useEffect(() => {
        const fullKizList = documentData.items
            .flatMap(item => item.kizConfirmationScanned || []);
        const trusted = fullKizList.filter(kiz => kiz.infoScanned && kiz.infoScanned.length > 0);

        setScanMatchMode(trusted.length > 0);
    }, [documentData.items])

    useEffect(() => {
        refreshCurrentDisplayItems();
    }, [documentData.trustedSupplier])

    const operativeReserve = useOperativeReserve();

    const isValidDocument = (items:IActDisagregationItemEditDTO[], showMessage: () => void): boolean => {
        var flag = true;
        if (!items || items.length === 0)
        {
            flag = false;
            showMessage();
        }
        return flag;
    }

    const getItemForKiz = (): IActDisagregationItemCreateDTO => {
        return showMessageModalWarning.item ??
            documentData.items.find((x) => x.idGlobal === selectedItem?.idGlobal) as IActDisagregationItemCreateDTO
    }

    const isKizBoxScan = (): boolean => {
        let item = getItemForKiz()
        return !documentData.isUnpackRecursive || (!item.kizBoxes) || (item.kizBoxes.length === 0);
    }

    const kizBatchTrusted = async () => {
        const fullKizList = documentData.items
            .flatMap(item => item.kizConfirmationScanned || []);

        const trusted = fullKizList.filter(kiz => kiz.infoScanned && kiz.infoScanned.length > 0);
        const distrusted = fullKizList.filter(kiz => !kiz.infoScanned || kiz.infoScanned.length === 0);

        const isKizBox = !documentData.isUnpackRecursive;

        trusted.length > 0 && await actDisagregationKizBatchTrusted(trusted.map(x => x.idGlobal), true, isKizBox);
        distrusted.length > 0 && await actDisagregationKizBatchTrusted(distrusted.map(x => x.idGlobal), false, isKizBox);
    }

    const onOkAction = () => {
        lockingDocuments.delete({
            idTable: IdTableVariant.ActDisagregation,
            idDocument: documentData.idGlobal as string,
        });

        props.ok(documentData);
    };

    const onCancel = () => {

        lockingDocuments.delete({
            idTable: IdTableVariant.ActDisagregation,
            idDocument: documentData.idGlobal as string,
        });

        if (!onlyRead)
            operativeReserve.deleteAll(
                {
                    idTable: IdTableVariant.ActDisagregation,
                    idDocument: documentData.idGlobal as string,
                },
                props.cancel
            );
        else
            props.cancel();

    };

    const renderProgressView = (actionMessage: string) => {
        setModalWindow(
            <ProgressActionSpinner
                loaderText={`${actionMessage}`}
            />
        );
    };
    
    const loadGoodsGnvls = async (idGoodsGlobal: string): Promise<IGoodsGnvls> => {
        const loadGoods = new Promise<IGoodsDTO>((resolve) => {
            goodsDP.getById(idGoodsGlobal, (goods) => {
                resolve(goods);
            });
        });

        const goods: IGoodsDTO= await Promise.resolve(loadGoods);
        return goods?.gnvls;
    };

    const makeRowsFromLot = async (lot: ILotDTO): Promise<IReturnCreatedItem[]> => {
        renderProgressView("Формирование строк документа"); // TODO: обработка fail-callbacks чтобы скрыть окно

        const x: ILotDTO = lot;

        // Подгрузка признака GNVLS
        const gnvlsMark: IGoodsGnvls = await loadGoodsGnvls(x.goods.idGlobal);

        // Разгруппировка партий по коробам
        const result: IReturnCreatedItem[] = [];

        // Собираем все коробки из всех items
        let allKizBoxes: IKizBoxDTO[] = [];
        
        // Сначала загружаем все коробки
        const loadAllKizBoxes = new Promise<IKizBoxDTO[]>((resolve) => {
            kizDocumentDP.getKizByItem(lot.documentInfo?.idDocument, lot.documentInfo.idDocumentItem, (entity) => {
                if (entity?.items?.length > 0) {
                    // Собираем все коробки из всех items
                    allKizBoxes = entity.items.reduce((boxes: IKizBoxDTO[], item) => {
                        if (item.kizBoxes?.length > 0) {
                            const procBoxes = item.kizBoxes.filter(box => box.kizState === "Proc");
                            return [...boxes, ...procBoxes];
                        }
                        return boxes;
                    }, []);
                }
                resolve(allKizBoxes);
            });
        });

        // Ждем загрузки всех коробок
        allKizBoxes = await Promise.resolve(loadAllKizBoxes);

        // Проверяем, что у нас достаточно коробок для разбиения
        if (allKizBoxes.length === 0) {
            console.warn("Не найдены коробки для разбиения");
            setModalWindow(<></>);
            return result;
        }

        let i: number = 0;
        while (i < allKizBoxes.length) {
            const idGlobal: string = uuidv4();
            const retailPriceIncVat: number = !gnvlsMark?.important ?
                x.retailCostInfo?.priceIncVat ? x.retailCostInfo?.priceIncVat / x.numerator : 0 :
                getGnvlsPriceFromLotValues(x.retailCostInfo?.priceIncVat, x.numerator, x.denominator);

            const valueItem: IReturnCreatedItem = {
                displayItem: {
                    idGlobal: idGlobal,
                    idLotFromGlobal: x.idGlobal,
                    goodsName: x.goods.displayName,
                    quantityFrom: 1,
                    scalingRatioTo: "",
                    scalingRatioFrom: x.scalingRatio.displayName,    
                    goodsCode: x.goodsCode,
                    producer: x.producer ? x.producer.displayName : "",                
                    retailPrice: x.retailCostInfo?.price ? x.retailCostInfo?.price / x.numerator : 0,
                    supplierPrice: x.supplierCostInfo?.price ? x.supplierCostInfo?.price / x.numerator : 0,
                    supplierSum: x.supplierCostInfo?.sum ? x.supplierCostInfo?.sum / (x.quantityRem + x.quantityRes): 0,
                    retailSumWithVat: x.retailCostInfo?.sumIncVat ? x.retailCostInfo?.sumIncVat / (x.quantityRem + x.quantityRes) : 0,
                    retailPriceIncVat: retailPriceIncVat,
                    lotRetailPriceIncVat: x.retailCostInfo?.priceIncVat ?? 0,
                    quantity: x.numerator,
                    isKiz: x.isKizFlag,
                    isGnvls: gnvlsMark?.important,
                    countKizScan: !x.isKiz ? undefined : 0,
                    dateCreated: DateTime.now(),
                    financialSecurityCode: x?.financialSecurity?.displayName
                } as IDisplayItem,
                createdItem: {
                    idGlobal: idGlobal,
                    idGoodsGlobal: x.goods.idGlobal,
                    idScalingRatioToGlobal: "",
                    producerPrice: x.producerPrice,
                    goodsCode: x.goodsCode,
                    producer: x.producer ? x.producer.displayName : "",
                    idScalingRatioFromGlobal: x.scalingRatio.idGlobal,
                    retailCostInfo: {
                        adprice: x.retailCostInfo?.adprice ?? 0,
                        price: x.retailCostInfo?.price ? x.retailCostInfo?.price / x.numerator : 0,
                        priceIncVat: retailPriceIncVat,
                        sum: x.retailCostInfo?.sum ? x.retailCostInfo?.sum / (x.quantityRem + x.quantityRes) : 0,
                        sumIncVat: x.retailCostInfo?.sumIncVat ? x.retailCostInfo?.sumIncVat / (x.quantityRem + x.quantityRes) : 0,
                        vat: x.retailCostInfo?.vat ?? 0,
                        vatPrice: x.retailCostInfo?.vatPrice ? x.retailCostInfo?.vatPrice / x.numerator : 0,
                        vatSum: x.retailCostInfo?.vatSum ? x.retailCostInfo?.vatSum / (x.quantityRem + x.quantityRes) : 0
                    } as ICostInfoDTO,
                    supplierCostInfo: {
                        adprice: x.supplierCostInfo?.adprice ?? 0,
                        price: x.supplierCostInfo?.price ? x.supplierCostInfo?.price / x.numerator : 0,
                        priceIncVat: x.supplierCostInfo?.priceIncVat ? x.supplierCostInfo?.priceIncVat / x.numerator : 0,
                        sum: x.supplierCostInfo?.sum ? x.supplierCostInfo?.sum / (x.quantityRem + x.quantityRes) : 0,
                        sumIncVat: x.supplierCostInfo?.sumIncVat ? x.supplierCostInfo?.sumIncVat / (x.quantityRem + x.quantityRes) : 0,
                        vat: x.supplierCostInfo?.vat ?? 0,
                        vatPrice: x.supplierCostInfo?.vatPrice ? x.supplierCostInfo?.vatPrice / x.numerator : 0,
                        vatSum: x.supplierCostInfo?.vatSum ? x.supplierCostInfo?.vatSum / (x.quantityRem + x.quantityRes) : 0
                    } as ICostInfoDTO,
                    idLotFromGlobal: x.idGlobal,
                    quantityFrom: 1,
                    quantity: x.numerator,
                    productMargin: x.productMargin ?? 0,
                    kizCount: 0,
                    kizBoxCount: 0,
                    supplier: x.supplier,
                    numerator: x.numerator,
                    denominator: x.denominator,
                    gtdNumber: x.gtdNumber,
                    isGnvls: gnvlsMark?.important,
                    dateCreated: DateTime.now()
                } as IActDisagregationItemCreateDTO,
            } as IReturnCreatedItem;

            // Берем короб из собранного массива
            const kizBoxItem: IKizBoxDTO = allKizBoxes[i];
            
            // Создаем editItem с коробом
            let editItem = { 
                ...valueItem.createdItem, 
                kizBoxes: [kizBoxItem] 
            } as IActDisagregationItemEditDTO;

            // Подгрузка КИЗ, связанных с коробом
            // только для рекурсивного разбиения
            if (documentData.isUnpackRecursive && kizBoxItem) {
                const loadKizs = new Promise<IActDisagregationItemEditDTO>((resolve) => {
                    kizDP.getKizBySscc(kizBoxItem.sscc, (kizItems: IKizDTO[]) => {
                        const itemWithKizs = { 
                            ...editItem, 
                            kizs: kizItems 
                        } as IActDisagregationItemEditDTO;
                        resolve(itemWithKizs);
                    });
                });
                
                editItem = await Promise.resolve(loadKizs);
            }

            // Подгрузка Scaling Ratio из Goods
            const loadScalingRatioFromGoods = function(item: IActDisagregationItemEditDTO) {
                return new Promise<IActDisagregationItemEditDTO>(function (resolve) {
                    scalingRatioDP.overrideGetView(x.goods.idGlobal,
                        {
                            pageNumber: 1,
                            numberPerPage: 1,
                            columnFilters: [
                                { name: "numerator", operator: "Eq", value: "1" } as IViewFilter, 
                                { name: "denominator", operator: "Eq", value: "1" } as IViewFilter
                            ],
                        } as IViewBody,
                        (scalingRatioViews: IScalingRatioViewDTO[]) => {
                            if (scalingRatioViews.length > 0) {
                                scalingRatioDP.overrideGetById(x.goods.idGlobal, scalingRatioViews[0].idGlobal, (sr) => {
                                    item = {...item, idScalingRatioToGlobal: sr.idGlobal}
                                    valueItem.displayItem.scalingRatioTo = sr.displayName;
                                    resolve(item);
                                });
                            } else {
                                resolve(item);
                            }
                        }
                    );
                });
            }

            editItem = await Promise.resolve(loadScalingRatioFromGoods(editItem));
            result.push({...valueItem, createdItem: editItem});
            
            i += 1;
        };

        // Если коробок меньше, чем требуется по numerator, выводим предупреждение
        if (allKizBoxes.length < x.numerator) {
            console.warn(`Найдено ${allKizBoxes.length} коробок, но требуется ${x.numerator}`);
        }

        setModalWindow(<></>);
        return result;
    };

    return (
        <BaseDocumentCreator
            valid={true}
            ok={{
                onClick: async () => {
                    if (
                        isValid() &&
                        isValidDocument(documentData.items, ()=> setShowMessageModalValidDocument({message: errorsT("validDocument"), show: true })) === true &&
                        controlKiz(documentData.items, () => setShowMessageModal({ message: errorsT("kizCountNotMatch"), show: true, btSaveOrOk: "ok" }), DocumentType.disaggregation) === true
                    ) {
                        await kizBatchTrusted();
                        onOkAction();
                    }
                },
                disabled: onlyRead || props.isSubmitting,
                sendRequestGlobal: props.isSubmitting
            }}
            save={{
                onClick: async () => {
                    if (
                        isValid() &&
                        isValidDocument(documentData.items, ()=> setShowMessageModalValidDocument({message: errorsT("validDocument"), show: true })) === true &&
                        controlKiz(documentData.items, () => setShowMessageModal({ message: errorsT("kizCountNotMatch"), show: true, btSaveOrOk: "save" }), DocumentType.disaggregation) === true
                    ) {
                        await kizBatchTrusted();
                        props.save(documentData);
                    }
                },
                disabled: onlyRead || props.isSubmitting,
                sendRequestGlobal: props.isSubmitting
            }}
            cancel={{ onClick: onCancel }}
            print={{
                onClick: () => {
                    return IdTableVariant.ActDisagregation;
                },
                onSubItemClick: () => {
                    let arr = new Array<IKeyValuePair>();
                    if (props.document != undefined && props.document.mnemocode != undefined && props.idGlobal != undefined)
                        arr.push({ key: props.idGlobal, value: props.document.mnemocode });

                    return arr;
                },
            }}
        >
            <div className={styles.glwrapper}>
                <div className={styles.wrapper}>
                    <div>
                        <GridWrapper cols={5} baseline>
                            <TextInput
                                labelClassName={styles.numberLabel}
                                disabled={true}
                                label={baseT("number")}
                                inputId={"inputInvoiceNumber"}
                                inline={true}
                                value={props.variant == "edit" && props.document ? props.document.mnemocode : undefined}
                            />
                            <DateInput
                                required
                                disabled={onlyRead ? true : false}
                                labelVariant="required"
                                inputId={"inputInvoiceDate"}
                                label={baseT("date")}
                                value={documentData.documentDate}
                                onBlur={(value) => setDocumentData({ ...documentData, documentDate: value as DateTime })}
                                inline={true}
                                error={errors.documentDate}
                            />
                            <CheckBox
                                defaultChecked={documentData.isNewBarcode}
                                onChanged={() => setDocumentData({ ...documentData, isNewBarcode: !documentData.isNewBarcode })}
                                id={uuidv4()}
                                label={baseT("isNewBarcode")}
                                disabled={onlyRead}
                            />
                            <CheckBox
                                defaultChecked={documentData.trustedSupplier}
                                onChanged={() => setDocumentData({ ...documentData, trustedSupplier: !documentData.trustedSupplier })}
                                id={uuidv4()}
                                label={baseT("trustedSupplier")}
                                disabled={onlyRead || scanMatchMode || !(isAdministrator || hasTrustedSupplierFullAccess)}
                                clues={[baseT("clueTextTrustedSupplier")]}
                            />
                            <CheckBox
                                defaultChecked={!documentData.isUnpackRecursive}
                                onChanged={() => setDocumentData({ ...documentData, isUnpackRecursive: !documentData.isUnpackRecursive })}
                                id={uuidv4()}
                                label={baseT("partlyDisagregation")}
                                disabled={onlyRead || disagregationMode}
                                className={styles.partlyLabel}
                                clues={[baseT("clueTextPartlyDisagregation")]}
                            />
                            
                        </GridWrapper>
                        <GridWrapper cols={3}>
                            <StoreSelectorPanel
                                compactMode
                                we
                                required
                                idTable={idTable}
                                change={onCanChangeData(documentData.items.length)}
                                storeFromLot={baseFields.storeFromLot}
                                contractor={{
                                    label: baseT("pharmacy"),
                                    onSelect: (selectedContractor: IEntitySimpleDTO) => {
                                        if (selectedContractor) {
                                            setBaseFields({ ...baseFields, pharmacy: selectedContractor })
                                        }
                                    },
                                    onClear: () => setBaseFields({ ...baseFields, pharmacy: {} as IEntitySimpleDTO }),
                                    disabled: onlyRead,
                                    treeViewCheckDirectoryType:'pharmacy'
                                }}
                                store={{
                                    label: baseT("store"),
                                    value: baseFields.store,
                                    onSelect: (value) => value && setDocumentData({ ...documentData, idStoreGlobal: value.idGlobal }),
                                    onClear: () => setDocumentData({ ...documentData, idStoreGlobal: undefined }),
                                    error: errors.store,
                                    onFocus: () => setErrors({ ...errors, store: undefined }),
                                    disabled: onlyRead,
                                    className: styles.defaultMargin
                                }}
                                notAutoComplete={props.variant === "edit" || props.variant === "copy"}
                                documentStore={props.document?.store}
                            />
                            <DictionaryInput
                                placeholder={baseT("checkBoxNotSelected")}
                                label={baseT("baseDocument")}
                                value={displayNameBaseDoc}
                                onClearClick={() =>  {
                                    setDisplayNameBaseDoc("")
                                    setDocumentData({...documentData, idBaseDocumentGlobal: undefined})
                                }}
                                onMoreClick={() => {
                                    !baseFields.store?.idGlobal ? setShowMessageModalValidBaseDocument(true) : setSelectBaseDocument(true);
                                }}
                                disabled={onlyRead || !documentData.isUnpackRecursive}
                            />
                            
                        </GridWrapper>
                        <Accordion opened={false} caption={baseT("gosContract")} id={`gos-${uuidv4()}`}>
                            <GridWrapper cols={2}>
                                <GridSelectorInput selectorModalJsx={StoreSelectorModal} id={"dictionaryInputRevaluationStore"} inline={true} label={baseT("gosContract")} disabled />
                                <GridSelectorInput selectorModalJsx={StoreSelectorModal} id={"dictionaryInputRevaluationStore"} inline={true} label={baseT("targetProgram")} disabled />
                            </GridWrapper>
                        </Accordion>
                        <FlexRow className={styles.field_flexblock}>
                            <PositionEditPanel
                                kiz={{
                                    onClick: () => setShowKizScan(true),
                                    disabled: !enableKizScan || !(selectedItem && documentData.items.length > 0 && !onlyRead),
                                }}
                                edit={{
                                    onClick: () => {
                                        if (selectedItem) {
                                            const editItem = documentData.items.find((x) => x.idGlobal === selectedItem.idGlobal) as IActDisagregationItemEditDTO;
                                            lotDP.getById(editItem.idLotFromGlobal, (lot: ILotDTO) => {

                                                contractorDP.getById(lot.contractor?.idGlobal, (itemContractor: IContractorDTO) => {
                                                    setCreatorItem({
                                                        show: true,
                                                        item: { ...lot },
                                                        editItem: editItem,
                                                        mode: 'edit',
                                                        trusted: itemContractor.attributesInfo.trustedSupplier,
                                                        autoConfirm: false
                                                    });
                                                });
                                            });
                                        }
                                    },
                                    disabled: !(selectedItem && !onlyRead),
                                }}
                                delete={{
                                    onClick: () => {
                                        if (selectedItem) {
                                            let item = documentData.items.find((x) => x.idGlobal === selectedItem.idGlobal);
                                            if (item) {
                                                operativeReserve.delete(
                                                    {
                                                        idLot: item.idLotFromGlobal,
                                                        idTable: IdTableVariant.ActDisagregation,
                                                        idDocument: documentData.idGlobal as string,
                                                        idDocumentItem: selectedItem.idGlobal as string,
                                                    },
                                                    () => {
                                                        setDocumentData({ ...documentData, items: documentData.items.filter((x) => x.idGlobal !== selectedItem.idGlobal) });
                                                        setKizs(undefined)
                                                        setKizBoxes(undefined)
                                                        setDisplayItems([...displayItems.filter((x) => x.idGlobal !== selectedItem.idGlobal)]);
                                                        setSelectedItem(undefined);
                                                    }
                                                );
                                            }
                                        }
                                    },
                                    disabled: !(selectedItem && !onlyRead),
                                }}
                            />
                            <GridSelectorInput
                                disabled={onlyRead}
                                barcodeScan={tabsContext.currentTab && tabsContext.currentTab?.id === currentIdTab.current}
                                selectorModalJsx={LotsSelectorModal}
                                className={styles.field_SelectionGoods}
                                idTable={idTable}
                                id={"inputAccompanyingSelectionGoods"}
                                gridFilter={gridFilterLot}
                                dispatchGridFilter={dispatchGridFilterLot}
                                inline={true}
                                label={baseT("selectionOfGoods")}
                                onClear={() => {}}
                                autoFocus={autoFocus}
                                onSelect={(value, searchQuery) => {
                                    lotDP.getById(value.idGlobal, (lot) => {
                                        if (!checkStorePharmacy(lot, baseFields.store, baseFields.pharmacy)) {
                                            return;
                                        }

                                        let items = [] as IActDisagregationItemEditDTO[]
                                        let itemDuplicate: IActDisagregationItemEditDTO | undefined
                                        if (searchOption.value === SearchOption.kizBox) {
                                            setScanData(searchQuery as string);
                                            items = documentData.items.filter(c => c.idLotFromGlobal === lot.idGlobal && (!c.kizBoxes || c.kizBoxes.length === 0))
                                            itemDuplicate = documentData.items.find(c => c.kizBoxes?.find(b => b.sscc === searchQuery?.substring(2))?.sscc) ?? undefined
                                        }

                                        if (itemDuplicate) duplicateMessage()
                                        else if (items.length > 0) addKizInExistingItem(items)
                                        else {
                                            contractorDP.getById(lot.idSupplierGlobal, (itemContractor: IContractorDTO) => {
                                                setCreatorItem(
                                                    {
                                                        show: true,
                                                        item: { ...lot },
                                                        trusted: itemContractor.attributesInfo.trustedSupplier,
                                                        mode: 'init'
                                                    });
                                            });
                                        }
                                    });
                                }}
                                searchOption={searchOption.value}
                                onChange={(e) => setScanData(e)}
                                idStoreGlobal={documentData.idStoreGlobal}
                            />
                            <Select
                                disabled={onlyRead}
                                barcodeScan={tabsContext.currentTab && tabsContext.currentTab?.id === currentIdTab.current}
                                onSelect={(option) => setSearchOption(option)}
                                value={searchOption}
                                defaultOption={defaultSearchOption}
                                className={styles.field_SelectBlock}
                                options={[
                                    { displayName: baseT("goodsName"), value: SearchOption.goodsName },
                                    { displayName: baseT("kizOrKizBox"), value: SearchOption.kizBox },
                                    { displayName: baseT("goodsCodeBylot"), value: SearchOption.goodsCodeBylot},
                                ]}
                            />
                        </FlexRow>
                        <ChangeableGrid
                            documentStateProc={onlyRead}
                            autoSelect
                            gridId={uuidv4()}
                            data={displayItems}
                            documentType={DocumentType.disaggregation}
                            hiddenPagination={undefined}
                            filter={gridFilter}
                            totalCount={displayItems.length}
                            plugin={ActDisagregationItemCreatorGridPluginSettings}
                            selectedItem={selectedItem}
                            onSelect={(row) => setSelectedItem(row)}
                            onDoubleClick={(row) => props.onDoubleClick?.(row)}
                            onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                            onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                            onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                            onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                            searching
                        />
                    </div>
                    <Accordion opened={false} caption="" id={`accompanyingInvoiceInfo-${uuidv4()}`} bold documentSticky>
                        <GridWrapper cols={3}>
                            <MoneyFormatComponent inline disabled labelClassName="label__width-111" label={baseT("supplierAmounts")} inputId={"inputAccompanyingNdsSum"} value={sum.amountSupplierSum} />
                            <MoneyFormatComponent inline disabled labelClassName="label__width-111" label={baseT("supplierVatAmounts")} inputId={"inputAccompanyingNdsProviderSum"} value={sum.amountSupplierVatSum} />
                            <MoneyFormatComponent inline disabled label={baseT("supplierAmountsWithVat")} inputId={"inputAccompanyingProviderSum"} value={sum.amountSupplierSumIncVat} />
                        </GridWrapper>

                        <GridWrapper cols={3}>
                            <MoneyFormatComponent inline disabled labelClassName="label__width-111" label={baseT("retailAmount")} inputId={"inputAccompanyingRetailSumBNds"} value={sum.amountRetailSum} />
                            <MoneyFormatComponent inline disabled labelClassName="label__width-111" label={baseT("retailVatAmount")} inputId={"inputAccompanyingNdsRetailSumNds"} value={sum.amountRetailVatSum} />
                            <MoneyFormatComponent inline disabled label={baseT("retailAmountWithVat")} inputId={"inputAccompanyingRetailSum"} value={sum.amountRetailSumIncVat} />
                        </GridWrapper>
                    </Accordion>
                </div>
            </div>

            {showKizScan && (
                <KizScanModal
                    ok={(value) => {
                        let items: IActDisagregationItemEditDTO[] = documentData.items.filter((x) => x.idGlobal !== value?.idGlobal);
                        setDocumentData({ ...documentData, items: items ? [...items, {...value, dateCreated: DateTime.now()}] : [value] });
                        setDisplayItems(
                            displayItems.map((x): IDisplayItem => {
                                return {
                                    countKizScan: x.idGlobal === value.idGlobal ?
                                        countKizSum(value, DocumentType.disaggregation, documentData.isUnpackRecursive, documentData.trustedSupplier) :
                                        x.countKizScan,
                                    goodsName: x.goodsName,
                                    idGlobal: x.idGlobal,
                                    idLotFromGlobal: x.idLotFromGlobal,
                                    isGnvls: x.isGnvls,
                                    goodsCode: x.goodsCode,
                                    producer: x.producer,
                                    isKiz: x.isKiz,
                                    quantity: x.quantity,
                                    quantityFrom: x.quantityFrom,
                                    scalingRatioFrom: x.scalingRatioFrom,
                                    scalingRatioTo: x.scalingRatioTo,
                                    retailPriceIncVat: x.retailPriceIncVat,
                                    lotRetailPriceIncVat: x.lotRetailPriceIncVat,
                                    supplierPrice: x.supplierPrice,
                                    supplierSum: x.supplierSum,
                                    supplier: x.supplier,
                                    dateCreated: x.idGlobal === value.idGlobal ? DateTime.now() : x.dateCreated,
                                    financialSecurityCode: x?.financialSecurityCode
                                };
                            })
                        );
                        setShowKizScan(false);
                        setShowMessageModalWarning({...showMessageModalWarning, item: undefined})
                    }}
                    cancel={() => {
                        setShowKizScan(false)
                        setShowMessageModalWarning({...showMessageModalWarning, item: undefined})
                    }}
                    selectedItem={getItemForKiz()}
                    documentId={props.documentUpdate ? props.documentUpdate.idGlobal : undefined}
                    canScanKiz
                    canScanKizBox={isKizBoxScan()}
                    totalScanBoxCount={documentData.isUnpackRecursive ? getItemForKiz()?.quantityFrom : getItemForKiz()?.quantity}
                    totalScanKizCount={getScanKizCount()}
                    numerator={documentData.isUnpackRecursive ? (selectedScalingRatio?.numerator ?? 1) : scalingRatioTo?.numerator ?? 1}
                    documentType={DocumentType.disaggregation}
                    document={{ idTable: IdTableVariant.ActDisagregation, idDocument: documentData.idGlobal as string }}
                    idLotFrom={getItemForKiz().idLotFromGlobal ?? null}
                    onValidateDuplicate={(barcode, kiz, kizBoxes) => validateDuplicateKizs(documentData.items,barcode,DocumentType.disaggregation,kiz,kizBoxes)}
                    isPalette={!documentData.isUnpackRecursive}
                    confirmationMode={!documentData.trustedSupplier && !selectedItemWithTrusted?.trusted}
                />
            )}

            {showMessageModal.show && (
                <MessageModalWindow
                    message={showMessageModal.message}
                    ok={{
                        onClick: () => {
                            setShowMessageModal({ show: false, message: "" });
                            showMessageModal.btSaveOrOk === "ok" ? onOkAction() : props.save(documentData);
                        },
                    }}
                    cancel={{
                        onClick: () => {
                            setShowMessageModal({ show: false, message: "" });
                        },
                    }}
                    primary
                />
            )}

            {showMessageModalWarning.show && (
                <MessageModalWindow
                    message={showMessageModalWarning.message}
                    ok={{
                        onClick: () => {
                            setShowMessageModalWarning({ ...showMessageModalWarning, show: false, message: "" });
                            setShowKizScan(true);
                        },
                    }}
                    cancel={{
                        onClick: () => {
                            setShowMessageModalWarning({ show: false, message: "", item: undefined });
                        },
                    }}
                    primary
                />
            )}

            {showMessageModalValidDocument.show && (
                <MessageModalWindow
                    message={showMessageModalValidDocument.message}
                    ok={{
                        onClick: () => {
                            setShowMessageModalValidDocument({ show: false, message: "" });
                        },
                    }}
                    cancel={{ onClick: () => setShowMessageModalValidDocument({ show: false, message: "" }) }}
                />
            )}

            {creatorItem.show && creatorItem.item && creatorItem?.editItem && (
                <ActDisagregationUpdateItemModal
                    autoConfirm={creatorItem.autoConfirm || false}
                    ok={async (createItem: ICreateItem, postProcess?: () => void, fallback?: () => void) => {

                        const { item, displayItem } = createItem;

                        const updateDataItems = (item: ICreateItem['item']) => {
                            setDocumentData((prevState) => ({
                                ...prevState,
                                items: prevState.items.some(el => el.idGlobal === item.idGlobal)
                                    ? prevState.items.map(el =>
                                        el.idGlobal === item.idGlobal ? { ...el, ...item } : el
                                    )
                                    : [...prevState.items, item]
                            }));
                        };

                        const updateDisplayItems = async (
                            displayItem: ICreateItem['displayItem'],
                            item: ICreateItem['item']
                        ) => {

                            const trusted = await checkIfTrusted(item.idGlobal, item.idLotFromGlobal, fallback);

                            let countKizScan = 0;
                            if (documentData.isUnpackRecursive) {
                                countKizScan = documentData.trustedSupplier || trusted
                                    ? kizs?.length ?? 0
                                    : item.kizConfirmationScanned?.length ?? 0;
                            } else {
                                countKizScan = documentData.trustedSupplier || trusted
                                    ? kizBoxes?.length ?? 0
                                    : item.kizConfirmationScanned?.length ?? 0;
                            }
                            setDisplayItems((prevDisplayItems) => {
                                if (prevDisplayItems.some(el => el.idGlobal === displayItem.idGlobal)) {
                                    return prevDisplayItems.map(el =>
                                        el.idGlobal === displayItem.idGlobal
                                            ? { ...el, ...displayItem, countKizScan }
                                            : el
                                    );
                                }

                                return [
                                    ...prevDisplayItems,
                                    { ...displayItem, countKizScan }
                                ];
                            });
                        };

                        const resetForm = () => {
                            setCreatorItem({ show: false, item: null, editItem: null, trusted: false });
                            setBaseFields({ ...baseFields, storeFromLot: creatorItem.item?.store as IEntitySimpleDTO });
                        };

                        const resetKizStates = () => {
                            setKizs(undefined);
                            setKizBoxes(undefined);
                        };

                        const showWarningIfNeeded = (item: ICreateItem['item']) => {
                            if (!item.kizBoxes || item.kizBoxes.length === 0) {
                                setShowMessageModalWarning({
                                    show: true,
                                    message: errorsT("warningMessage"),
                                    item: item
                                });
                            }
                        };

                        sendOnlineReserve(item.idLotFromGlobal, item.idGlobal, 1, async () => {
                            // Обновляем documentData.items
                            updateDataItems(item);

                            // Обновляем displayItems
                            await updateDisplayItems(displayItem, item);

                            // Сбрасываем форму и поля
                            resetForm();

                            // Сбрасываем данные по КИЗ
                            resetKizStates();

                            // Показываем предупреждение при отсутствии кизов
                            showWarningIfNeeded(item);

                            // Загружаем Scaling Ratio
                            if (!documentData.isUnpackRecursive) {
                                loadScalingRatioTo(item.idGoodsGlobal);
                            }

                            postProcess?.()
                        },
                            fallback);

                        setScalingRatioTo(undefined);
                    }}
                    cancel={() => setCreatorItem({ show: false, item: null, editItem: null, trusted: false })}
                    itemLot={creatorItem.item}
                    itemEdit={creatorItem.editItem}
                    pricingModel={pricingModel}
                    partlyMode={!(documentData.isUnpackRecursive as boolean)}
                    editMode={creatorItem.mode === "edit"}
                />
            )}

            {showMessageModalValidBaseDocument &&
                <MessageModalWindow
                    message={t("errors.noStoreForBaseDocument")}
                    ok={{ onClick: () => setShowMessageModalValidBaseDocument(false) }}
                    cancel={{ onClick: () => setShowMessageModalValidBaseDocument(false) }}
                />
            }

            {selectBaseDocument && (
                <SelectDocumentModalWindow
                    idStoreGlobal={documentData.idStoreGlobal ?? uuidNull}
                    dateDocument={documentData.documentDate}
                    ok={(lots, document) => {
                        setSelectBaseDocument(true);
                        setDocumentData({ ...documentData, items: [] });

                        const getCreatedItemsPromises: Promise<IReturnCreatedItem[]>[] = lots.filter(l => l.isKizBox).flatMap(async (x) => {
                            const items = await makeRowsFromLot(x);
                            return items;
                        });

                        Promise.all(getCreatedItemsPromises).then((values) => {
                            setDisplayNameBaseDoc(document.displayName);
                            setDocumentData({ ...documentData, items: values.flat().map((x) => x.createdItem), idBaseDocumentGlobal: document.idGlobal});
                            setDisplayItems(values.flat().map((x) => x.displayItem));
                            setSelectBaseDocument(false);
                        })
                    }}
                    cancel={{ onClick: () => setSelectBaseDocument(false) }}
                />
            )}

            {modalWindow}

        </BaseDocumentCreator>
    );
};
